import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Heart, Inbox, User as UserIcon, Loader2, ExternalLink, MapPin, Phone, Bookmark, Mail, ShieldCheck } from "lucide-react";
// SavedProviders displays the user's bookmarked providers as full cards.
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import StarRating from "@/components/StarRating";
import ProviderCard from "@/components/ProviderCard";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function VerifyEmailBanner({ user }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
      toast.success("Verification email sent. Check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send verification email. Please try again in a minute.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="verify-email-banner">
      <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Mail className="w-4 h-4 text-amber-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-amber-900 text-sm">Please verify your email address</div>
        <p className="text-xs text-amber-800 mt-0.5">
          We sent a verification link to <strong>{user.email}</strong>. Verify to make sure you receive booking confirmations and replies.
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-300 text-amber-800 hover:bg-amber-100"
        disabled={sending || sent}
        onClick={resend}
        data-testid="resend-verification-btn"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : sent ? <><ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Sent</> : "Resend email"}
      </Button>
    </div>
  );
}

export default function UserDashboard() {
  const { user } = useAuth();
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-1">My Account</div>
        <h1 className="font-display text-3xl font-semibold">Hi {user?.name?.split(" ")[0]}</h1>
        <p className="text-stone-600 mt-1">Your reviews, appointment requests, and saved providers.</p>
      </div>

      <VerifyEmailBanner user={user} />

      <Tabs defaultValue="reviews">
        <TabsList>
          <TabsTrigger value="reviews" data-testid="user-tab-reviews"><Star className="w-3.5 h-3.5 mr-1.5" /> My Reviews</TabsTrigger>
          <TabsTrigger value="saved" data-testid="user-tab-saved"><Bookmark className="w-3.5 h-3.5 mr-1.5" /> Saved</TabsTrigger>
          <TabsTrigger value="leads" data-testid="user-tab-leads"><Inbox className="w-3.5 h-3.5 mr-1.5" /> Requests</TabsTrigger>
          <TabsTrigger value="profile" data-testid="user-tab-profile"><UserIcon className="w-3.5 h-3.5 mr-1.5" /> Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="reviews" className="mt-6"><MyReviews /></TabsContent>
        <TabsContent value="saved" className="mt-6"><SavedProviders /></TabsContent>
        <TabsContent value="leads" className="mt-6"><MyLeads /></TabsContent>
        <TabsContent value="profile" className="mt-6"><Profile user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

function MyReviews() {  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/reviews").then(({ data }) => setReviews(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;
  if (reviews.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
        <p className="text-stone-500 text-sm mb-4">You haven't written any reviews yet.</p>
        <Link to="/search" className="text-[#064E3B] text-sm font-semibold hover:underline">Browse providers →</Link>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {reviews.map((r) => (
        <div key={r.id} className="bg-white rounded-xl border border-stone-200 p-5 flex gap-4">
          {r.provider?.hero_image && <img src={r.provider.hero_image} alt="" className="w-20 h-20 rounded-lg object-cover hidden sm:block" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <Link to={`/listing/${r.provider?.slug}`} className="font-display font-semibold hover:text-[#064E3B]">{r.provider?.name}</Link>
                <div className="text-xs text-stone-500">{r.provider?.city}, {r.provider?.state}</div>
              </div>
              <StarRating value={r.rating} size={13} />
            </div>
            <div className="text-sm font-semibold text-stone-900 mt-2">{r.title}</div>
            <p className="text-sm text-stone-600 mt-1 line-clamp-2">{r.body}</p>
            <div className="text-xs text-stone-400 mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/leads").then(({ data }) => setLeads(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;
  if (leads.length === 0) {
    return <div className="text-center py-16 bg-white rounded-2xl border border-stone-200 text-stone-500 text-sm">No appointment requests yet.</div>;
  }
  return (
    <div className="space-y-3">
      {leads.map((l) => (
        <div key={l.id} className="bg-white rounded-xl border border-stone-200 p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <Link to={`/listing/${l.provider?.slug}`} className="font-display font-semibold hover:text-[#064E3B]">{l.provider?.name || "Provider"}</Link>
              <div className="text-xs text-stone-500 flex items-center gap-3 mt-0.5">
                {l.provider?.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{l.provider.city}</span>}
                {l.provider?.phone && <a href={`tel:${l.provider.phone}`} className="inline-flex items-center gap-1 hover:text-[#064E3B]"><Phone className="w-3 h-3" />{l.provider.phone}</a>}
              </div>
            </div>
            <Badge variant="outline" className={
              l.status === "new" ? "border-[#10B981] text-[#10B981]" :
              l.status === "booked" ? "border-[#064E3B] text-[#064E3B]" :
              "border-stone-300 text-stone-500"
            }>{l.status}</Badge>
          </div>
          {l.service && <div className="text-sm text-stone-700">Service: <strong>{l.service}</strong></div>}
          {l.preferred_date && <div className="text-sm text-stone-700">Preferred date: {l.preferred_date}</div>}
          <div className="text-xs text-stone-400 mt-2">Sent {new Date(l.created_at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function SavedProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/bookmarks").then(({ data }) => setProviders(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;
  if (providers.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
        <Bookmark className="w-10 h-10 text-stone-300 mx-auto mb-3" />
        <p className="text-sm text-stone-500 mb-4">No saved providers yet — tap the heart icon on any provider to save them here.</p>
        <Link to="/search" className="text-[#064E3B] text-sm font-semibold hover:underline">Browse providers →</Link>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {providers.map((p) => <ProviderCard key={p.id} p={p} />)}
    </div>
  );
}


function Profile({ user }) {  if (!user) return null;
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 max-w-lg">
      <div className="flex items-center gap-4 mb-5">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#064E3B] to-[#10B981] flex items-center justify-center text-white font-display font-semibold text-xl">
          {user.name?.[0]?.toUpperCase()}
        </div>
        <div>
          <div className="font-display text-lg font-semibold">{user.name}</div>
          <div className="text-sm text-stone-500">{user.email}</div>
        </div>
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-stone-500">Role</span><span className="font-medium capitalize">{user.role}</span></div>
        <div className="flex justify-between"><span className="text-stone-500">Member since</span><span className="font-medium">{new Date(user.created_at).toLocaleDateString()}</span></div>
      </div>
    </div>
  );
}
